import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Convert http/https to ws/wss
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [priceUpdates, setPriceUpdates] = useState({});
  const [predictionUpdates, setPredictionUpdates] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const subscribedCountryRef = useRef(null);

  // Message handlers
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      setLastMessage(message);

      switch (message.type) {
        case "connected":
          console.log("WebSocket connected:", message);
          break;

        case "price_update":
          // Single stock price update
          setPriceUpdates(prev => ({
            ...prev,
            [message.symbol]: message.data
          }));
          break;

        case "batch_prices":
          // Batch price updates
          const newPrices = {};
          (message.quotes || []).forEach(quote => {
            newPrices[quote.symbol] = quote;
          });
          setPriceUpdates(prev => ({ ...prev, ...newPrices }));
          break;

        case "prediction_update":
          // Prediction changed (locked, evaluated, refreshed)
          setPredictionUpdates(prev => [
            message.data,
            ...prev.slice(0, 19) // Keep last 20 updates
          ]);
          break;

        case "heartbeat":
          // Just keep the connection alive
          break;

        default:
          console.log("Unknown WebSocket message type:", message.type);
      }
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback((country = null) => {
    if (!isAuthenticated || !token) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    subscribedCountryRef.current = country;
    const wsUrl = country 
      ? `${WS_URL}/api/ws?token=${token}&country=${country}`
      : `${WS_URL}/api/ws?token=${token}`;

    console.log("Connecting to WebSocket...", wsUrl.replace(token, '***'));

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = handleMessage;

    ws.onclose = (event) => {
      console.log("WebSocket disconnected:", event.code, event.reason);
      setIsConnected(false);
      wsRef.current = null;

      // Attempt to reconnect if not a normal closure
      if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})...`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connect(subscribedCountryRef.current);
        }, delay);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        toast.error("WebSocket connection lost. Refresh page to reconnect.");
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  }, [isAuthenticated, token, handleMessage]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "User disconnected");
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Subscribe to a specific country
  const subscribeToCountry = useCallback((country) => {
    if (subscribedCountryRef.current !== country) {
      disconnect();
      connect(country);
    }
  }, [connect, disconnect]);

  // Send message to WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Clear price updates (useful when switching countries)
  const clearPriceUpdates = useCallback(() => {
    setPriceUpdates({});
  }, []);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, token]);

  const value = {
    isConnected,
    lastMessage,
    priceUpdates,
    predictionUpdates,
    connect,
    disconnect,
    subscribeToCountry,
    sendMessage,
    clearPriceUpdates
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
